import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, type ApiResponse } from "@/api/client";
import type { Order, OrderStatus } from "@ckgo2/shared/types";
import { formatPrice, formatDate } from "@ckgo2/shared/utils";
import {
  User,
  Calendar,
  Users,
  GripVertical,
  Eye,
  LayoutGrid,
  List,
} from "lucide-react";

interface OrderWithRelations extends Order {
  customer?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone?: string;
  };
  trip?: {
    id: string;
    name: string;
    slug: string;
    featuredImage?: string;
  };
  tripDate?: {
    id: string;
    startDate: string;
    endDate: string;
  };
}

const KANBAN_COLUMNS = [
  {
    id: "inquiry",
    label: "Poptávky",
    color: "bg-gray-500",
    bgColor: "bg-gray-50 dark:bg-gray-800/50",
  },
  {
    id: "quoted",
    label: "Naceneno",
    color: "bg-blue-500",
    bgColor: "bg-blue-50 dark:bg-blue-900/10",
  },
  {
    id: "confirmed",
    label: "Potvrzeno",
    color: "bg-purple-500",
    bgColor: "bg-purple-50 dark:bg-purple-900/10",
  },
  {
    id: "deposit_paid",
    label: "Záloha uhrazena",
    color: "bg-amber-500",
    bgColor: "bg-amber-50 dark:bg-amber-900/10",
  },
  {
    id: "fully_paid",
    label: "Zaplaceno",
    color: "bg-green-500",
    bgColor: "bg-green-50 dark:bg-green-900/10",
  },
  {
    id: "completed",
    label: "Dokončeno",
    color: "bg-emerald-500",
    bgColor: "bg-emerald-50 dark:bg-emerald-900/10",
  },
];

export default function OrderKanbanPage() {
  const queryClient = useQueryClient();
  const [draggedOrder, setDraggedOrder] = useState<OrderWithRelations | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["orders"],
    queryFn: () => api.get<ApiResponse<OrderWithRelations[]>>("/api/admin/orders"),
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      return api.patch(`/api/admin/orders/${id}/status`, { status });
    },
    // Optimistic update - immediately move the card
    onMutate: async ({ id, status }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ["orders"] });

      // Snapshot the previous value
      const previousOrders = queryClient.getQueryData<{ data: OrderWithRelations[] }>(["orders"]);

      // Optimistically update the cache
      queryClient.setQueryData<{ data: OrderWithRelations[] }>(["orders"], (old) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data.map((order) =>
            order.id === id ? { ...order, status: status as OrderStatus } : order
          ),
        };
      });

      // Return context with the previous value
      return { previousOrders };
    },
    // If the mutation fails, roll back to the previous value
    onError: (err, variables, context) => {
      if (context?.previousOrders) {
        queryClient.setQueryData(["orders"], context.previousOrders);
      }
      console.error("Failed to update order status:", err);
    },
    // Always refetch after error or success
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });

  const orders = data?.data || [];

  // Group orders by status
  const ordersByStatus = KANBAN_COLUMNS.reduce(
    (acc, column) => {
      acc[column.id] = orders.filter((o) => o.status === column.id);
      return acc;
    },
    {} as Record<string, OrderWithRelations[]>
  );

  // Count cancelled orders separately
  const cancelledCount = orders.filter((o) => o.status === "cancelled").length;

  const handleDragStart = (e: React.DragEvent, order: OrderWithRelations) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", order.id);
  };

  const handleDragOver = (e: React.DragEvent, columnId: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverColumn(columnId);
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    setDragOverColumn(null);

    if (draggedOrder && draggedOrder.status !== targetStatus) {
      updateStatusMutation.mutate({
        id: draggedOrder.id,
        status: targetStatus,
      });
    }

    setDraggedOrder(null);
  };

  const handleDragEnd = () => {
    setDraggedOrder(null);
    setDragOverColumn(null);
  };

  if (error) {
    return (
      <div className="card text-center text-error-600">
        Chyba při načítání objednávek.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Kanban objednávek
          </h1>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Přetažením karty změníte status objednávky
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Link to="/orders" className="btn-ghost">
            <List className="h-5 w-5" />
            Tabulka
          </Link>
          <Link to="/orders/kanban" className="btn-primary">
            <LayoutGrid className="h-5 w-5" />
            Kanban
          </Link>
        </div>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {KANBAN_COLUMNS.map((column) => (
          <div key={column.id} className="card">
            <div className="flex items-center gap-2">
              <div className={`h-3 w-3 rounded-full ${column.color}`} />
              <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
                {column.label}
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
              {ordersByStatus[column.id]?.length || 0}
            </p>
          </div>
        ))}
      </div>

      {cancelledCount > 0 && (
        <div className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
          {cancelledCount} zrušených objednávek (nezobrazeno v kanban)
        </div>
      )}

      {/* Kanban Board */}
      {isLoading ? (
        <div className="flex h-64 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {KANBAN_COLUMNS.map((column) => (
            <div
              key={column.id}
              className={`min-w-[300px] flex-1 rounded-xl ${column.bgColor} p-4 transition-all ${
                dragOverColumn === column.id
                  ? "ring-2 ring-primary-500 ring-offset-2"
                  : ""
              }`}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.id)}
            >
              {/* Column Header */}
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`h-3 w-3 rounded-full ${column.color}`} />
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    {column.label}
                  </h3>
                </div>
                <span className="rounded-full bg-white px-2 py-0.5 text-sm font-medium text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  {ordersByStatus[column.id]?.length || 0}
                </span>
              </div>

              {/* Cards */}
              <div className="space-y-3">
                {ordersByStatus[column.id]?.map((order) => (
                  <OrderCard
                    key={order.id}
                    order={order}
                    isDragging={draggedOrder?.id === order.id}
                    onDragStart={(e) => handleDragStart(e, order)}
                    onDragEnd={handleDragEnd}
                  />
                ))}

                {ordersByStatus[column.id]?.length === 0 && (
                  <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center text-sm text-gray-500 dark:border-gray-600">
                    Žádné objednávky
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

interface OrderCardProps {
  order: OrderWithRelations;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
}

function OrderCard({ order, isDragging, onDragStart, onDragEnd }: OrderCardProps) {
  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`group cursor-grab rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200 transition-all hover:shadow-md active:cursor-grabbing dark:bg-gray-800 dark:ring-gray-700 ${
        isDragging ? "opacity-50 ring-2 ring-primary-500" : ""
      }`}
    >
      {/* Header */}
      <div className="mb-3 flex items-start justify-between">
        <div className="flex items-center gap-2">
          <GripVertical className="h-4 w-4 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
          <div>
            <p className="font-medium text-gray-900 dark:text-white">
              {order.customer?.firstName} {order.customer?.lastName}
            </p>
            <p className="text-xs text-gray-500">{order.customer?.email}</p>
          </div>
        </div>
        <Link
          to={`/orders/${order.id}`}
          className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
          onClick={(e) => e.stopPropagation()}
        >
          <Eye className="h-4 w-4" />
        </Link>
      </div>

      {/* Trip */}
      <div className="mb-3">
        <p className="line-clamp-1 text-sm font-medium text-gray-700 dark:text-gray-300">
          {order.trip?.name}
        </p>
      </div>

      {/* Meta */}
      <div className="flex flex-wrap gap-2 text-xs text-gray-500">
        {order.tripDate && (
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {formatDate(order.tripDate.startDate)}
          </span>
        )}
        <span className="flex items-center gap-1">
          <Users className="h-3 w-3" />
          {order.adultsCount || 1} os.
        </span>
      </div>

      {/* Price */}
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-3 dark:border-gray-700">
        <span className="text-xs text-gray-500">Cena:</span>
        <span className="font-semibold text-primary-600">
          {formatPrice(order.totalPrice || 0)}
        </span>
      </div>
    </div>
  );
}
